import { toast } from 'sonner';

import { PrototypeFormDialog } from './prototype-form-dialog';
import {
  EMPTY_PROTOTYPE_FORM,
  toCreatePrototypePayload,
  type PrototypeFormValues,
} from './prototype-schemas';
import { useCreatePrototype } from './prototypes-queries';

export interface CreatePrototypeDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Create a prototype inside a project.
 *
 * Authorship comes from the session now (Phase 2): the submit handler builds
 * the payload and posts it, and the server records `createdBy` from who is
 * signed in. Access is gated at the button that opens this dialog — only a
 * Designer or Admin sees it — and the API enforces the rest.
 */
export const CreatePrototypeDialog = ({
  projectId,
  open,
  onOpenChange,
}: CreatePrototypeDialogProps) => {
  const createPrototype = useCreatePrototype();

  const handleSubmit = async (values: PrototypeFormValues): Promise<boolean> => {
    const prototype = await createPrototype.mutateAsync({
      projectId,
      payload: toCreatePrototypePayload(values),
    });

    toast.success(`Created “${prototype.name}”.`, {
      description: 'Upload a ZIP to give it its first version.',
    });

    return true;
  };

  return (
    <PrototypeFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New prototype"
      description="A prototype is one thing under review. Each upload becomes a numbered version of it."
      submitLabel="Create prototype"
      pendingLabel="Creating…"
      defaultValues={EMPTY_PROTOTYPE_FORM}
      onSubmit={handleSubmit}
    />
  );
};
